// ProfileDetails.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { APP_CONFIG } from "../../app-config";
import SidebarHeader from "../../components/SidebarHeader";
import { encryptDataHybrid, apiCall } from "../../utils/api";
import toast, { Toaster } from 'react-hot-toast';
import { FaUser, FaStore } from 'react-icons/fa';
import { FiArrowLeft } from "react-icons/fi";

const COLORS = APP_CONFIG.colors;
const currentYear = new Date().getFullYear();


function AgentRetailer() {
    const navigate = useNavigate();
    const [selectedRetailer, setSelectedRetailer] = useState(null);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [transferAmount, setTransferAmount] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [retailers, setRetailers] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchRetailer();
    }, [])

    const fetchRetailer = async () => {
        try {
            const result = await apiCall(
                "agent-retailer-list",
                {},
                true
            );

            if (result.status) {
                setRetailers(result.retailer);
                setLoading(false);
            } else {
                toast.error(result.message);
            }
        } catch (err) {
            toast.error(err.message);
            console.error("Error fetching commission:", err);
        }
    }

    const formatAmount = (amount) => {
        if (amount === undefined || amount === null) return '₹0.00';
        return `₹${parseFloat(amount).toFixed(2)}`;
    };

    const handleRetailerClick = (retailer) => {
        setSelectedRetailer(retailer);
        setShowTransferModal(true);
    };

    const handleTransferSubmit = async () => {
        if (!transferAmount || parseFloat(transferAmount) <= 0) {
            toast.error("Please enter a valid amount");
            return;
        }

        if (parseFloat(transferAmount) < 1) {
            toast.error("Please enter amount more than 1");
            return;
        }

        if (parseFloat(transferAmount) > 10000) {
            toast.error("Please enter amount greater than 10000");
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                amount: transferAmount,
                id: selectedRetailer.id
            }
            const result = await apiCall(
                "agent-wallet-transfer",
                payload,
                true
            );

            if (result.status) {
                toast.success(`₹${transferAmount} transferred to ${selectedRetailer.name} successfully!`);
                closeModal();
                setTransferAmount("");
                fetchRetailer();
            } else {
                toast.error(result.message);
            }
        } catch (err) {
            toast.error("Transfer failed. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeModal = () => {
        setIsClosing(true);
        setTimeout(() => {
            setShowTransferModal(false);
            setIsClosing(false);
            setSelectedRetailer(null);
            setTransferAmount("");
        }, 300);
    };

    if (loading) {
        return (
            <SidebarHeader>
                <div className="max-w-7xl mx-auto mb-16">
                    <div className="text-center py-12">
                        <div className="w-10 h-10 rounded-full animate-spin mx-auto mb-4" style={{ border: `3px solid ${COLORS.primaryColor}33`, borderTopColor: COLORS.primaryColor }} />
                        <div className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>
                            Loading retailer...
                        </div>
                    </div>
                </div>
                <Toaster />
            </SidebarHeader>
        );
    }

    return (
        <SidebarHeader>
            <div className="flex items-center gap-3 pb-4">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-200 hover:bg-gray-300 transition"
                >
                    <FiArrowLeft className="text-xl" />
                </button>
                <h1 className="text-lg font-semibold text-gray-800">
                    Agent - Retailer
                </h1>
            </div>
            <div className="max-w-7xl mx-auto mb-16">
                <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {retailers.map((retailer) => (
                            <div
                                key={retailer.id}
                                onClick={() => handleRetailerClick(retailer)}
                                className="flex items-center p-4 rounded-xl cursor-pointer transition-all hover:scale-[1.02]"
                                style={{
                                    background: COLORS.surfaceColor || '#ffffff',
                                    border: `1px solid ${COLORS.borderColor}`,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                                }}
                            >
                                {/* Left Side - Icon */}
                                <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0"
                                    style={{
                                        background: COLORS.primaryColor + '15',
                                        color: COLORS.primaryColor
                                    }}
                                >
                                    <FaStore size={18} />
                                </div>

                                {/* Middle - Name and Mobile */}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
                                        {retailer.name}
                                    </div>
                                    <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                                        +91 {retailer.mobile}
                                    </div>
                                </div>

                                {/* Right Side - Balance in Green */}
                                <div className="text-right flex-shrink-0 ml-2">
                                    <div className="text-sm font-bold" style={{ color: '#10b981' }}>
                                        {formatAmount(retailer.balance)}
                                    </div>
                                    <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                                        Balance
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>

                <Toaster />

                {/* Fund Transfer Centered Modal with Fade Animation */}
                {showTransferModal && selectedRetailer && (
                    <div
                        className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${isClosing ? 'opacity-0' : 'opacity-100'
                            }`}
                        onClick={closeModal}
                    >
                        {/* Backdrop */}
                        <div className="absolute inset-0 bg-black/50"></div>

                        {/* Modal Content */}
                        <div
                            className={`relative w-full max-w-md mx-4 p-6 rounded-2xl transform transition-all duration-300 ${isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
                                }`}
                            style={{
                                background: COLORS.surfaceColor || '#ffffff',
                                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Close Button */}
                            <button
                                onClick={closeModal}
                                className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 transition-colors"
                                style={{ color: COLORS.textSecondary }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>

                            <h3 className="text-lg font-bold mb-4" style={{ color: COLORS.textPrimary }}>
                                Fund Transfer
                            </h3>

                            {/* Retailer Info */}
                            <div className="mb-4 p-3 rounded-lg" style={{ background: COLORS.primaryColor + '10' }}>
                                <div className="flex items-center gap-3">
                                    <div
                                        className="w-10 h-10 rounded-full flex items-center justify-center"
                                        style={{
                                            background: COLORS.primaryColor + '15',
                                            color: COLORS.primaryColor
                                        }}
                                    >
                                        <FaUser size={18} />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
                                            {selectedRetailer.name}
                                        </div>
                                        <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                                            {selectedRetailer.mobile}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-2 text-sm">
                                    <span style={{ color: COLORS.textSecondary }}>Balance: </span>
                                    <span className="font-bold" style={{ color: '#10b981' }}>
                                        {formatAmount(selectedRetailer.balance)}
                                    </span>
                                </div>
                            </div>

                            {/* Amount Field */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium mb-2" style={{ color: COLORS.textPrimary }}>
                                    Enter Amount (₹)
                                </label>
                                <input
                                    type="number"
                                    value={transferAmount}
                                    onChange={(e) => setTransferAmount(e.target.value)}
                                    placeholder="Enter amount to transfer"
                                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                                    style={{
                                        borderColor: COLORS.borderColor,
                                        background: COLORS.backgroundColor || '#f9fafb',
                                        color: COLORS.textPrimary,
                                        fontSize: '16px'
                                    }}
                                    autoFocus
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3">
                                <button
                                    onClick={closeModal}
                                    className="flex-1 py-3 rounded-lg font-medium transition-colors"
                                    style={{
                                        background: '#e5e7eb',
                                        color: COLORS.textPrimary
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleTransferSubmit}
                                    disabled={isSubmitting}
                                    className="flex-1 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                    style={{
                                        background: COLORS.primaryColor,
                                        color: COLORS.textOnPrimary
                                    }}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <svg
                                                className="animate-spin h-5 w-5"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                />
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                />
                                            </svg>
                                            Processing...
                                        </>
                                    ) : (
                                        'Transfer'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </SidebarHeader>
    );
}

export default AgentRetailer;